import React from 'react';
import { isEmpty, isNil } from 'lodash';

import type { AiAction } from '~/components/AiChat/types';
import NotificationMessage from '~/components/ClaimPage/CliveApp/CliveNotifications/NotificationMessage';
import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import SafeComponent, { CriticalityLevel } from '~/components/core/SafeComponent';
import Text from '~/components/core/TextComponents/Text';

import { LOG_MESSAGE_VISIBLE_LINES } from '../../constants';
import { getSentAt, getToldVia } from '../../conversation/metadata';
import { CONVERSATION_TEXTS } from '../../conversation/texts';
import { actionTitle, formatEntryDateTime, formatEntryTime } from '../../utils';
import JumpLink from '../JumpLink';

interface HandlerTurnEntryProps {
  notification: StpNotification;
  /**
   * The card the handler decided, where the claim's actions carry it: as it stands, or as it stood then
   * where Clive has revised it since and its history holds that version.
   */
  action?: AiAction;
  /** Takes the reader to the reply Clive gave the message, where the log holds one. */
  onJumpToReply?: () => void;
}

/** When the handler sent the message and how the run was told it, on a turn that is a message to Clive. */
const messageMeta = (notification: StpNotification): string | null => {
  const sentTime = formatEntryTime(getSentAt(notification) ?? undefined);
  const toldVia = getToldVia(notification);
  const parts = [
    sentTime ? CONVERSATION_TEXTS.SENT_AT(sentTime) : null,
    toldVia ? CONVERSATION_TEXTS.TOLD_VIA[toldVia] : null,
  ].filter((part): part is string => !isNil(part));

  return isEmpty(parts) ? null : parts.join(CONVERSATION_TEXTS.SEPARATOR);
};

/**
 * A handler's turn: the note they wrote when rejecting a card, drawn as a bubble in the chat's human
 * colours. Not through `MessageLayout`: it caps a human bubble at 85% of the chat column and the log
 * wants the full width. Its error boundary is kept, so a note that fails to render takes down this
 * entry and not the claim page.
 *
 * A turn is either a person's rejection, whose card is a rejected one named from the joined row, or
 * from the version rejected where Clive has revised the card since, or a message the handler sent
 * Clive, which says when it was sent and how the run read it. `title` is the handler's name, and is
 * absent when the rejection recorded nobody — the entry then names nobody rather than inventing a
 * speaker.
 */
const HandlerTurnEntry: React.FC<HandlerTurnEntryProps> = ({ notification, action, onJumpToReply }) => {
  const { title, message, timestamp } = notification;
  const time = formatEntryDateTime(timestamp);
  const meta = messageMeta(notification);

  return (
    <SafeComponent location="ConductorHub/LogPanel/HandlerTurnEntry" criticalityLevel={CriticalityLevel.HIGH}>
      <div className="flex gap-12">
        <div className="flex w-20 shrink-0 justify-center pt-[5px]">
          <div className="h-[10px] w-[10px] rounded-full bg-azure-700" aria-hidden />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-8 rounded-xl border border-solid border-blue-500 bg-azure-500/10 p-16">
            <div className="flex flex-wrap items-baseline justify-between gap-12">
              {title || meta || onJumpToReply ? (
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-8">
                  {title ? (
                    <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="text-azure-700">
                      {title}
                    </Text>
                  ) : null}
                  {meta ? (
                    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                      {meta}
                    </Text>
                  ) : null}
                  {onJumpToReply ? (
                    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                      <JumpLink onClick={onJumpToReply}>{CONVERSATION_TEXTS.SEE_REPLY}</JumpLink>
                    </Text>
                  ) : null}
                </div>
              ) : null}
              {time ? (
                <Text
                  variant={Text.VARIANTS.XS}
                  colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                  className="whitespace-nowrap"
                >
                  {time}
                </Text>
              ) : null}
            </div>
            {action ? (
              <span className="w-fit rounded bg-azure-500/20 px-8 py-2">
                <Text variant={Text.VARIANTS.XS} className="text-azure-700">
                  {`Rejected: ${actionTitle(action)}`}
                </Text>
              </span>
            ) : null}
            {message ? (
              <NotificationMessage
                message={message}
                className="text-slate-900"
                visibleLines={LOG_MESSAGE_VISIBLE_LINES}
              />
            ) : null}
          </div>
        </div>
      </div>
    </SafeComponent>
  );
};

export default HandlerTurnEntry;
